import { useMemo } from "react";
import {
  ghostBtnClass,
  mfgInlineInputClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import type { VariantTransformRule } from "@/lib/manufacturing/variantConfig";
import {
  ManufacturingComboboxPicker,
  type ComboboxOption,
} from "./ManufacturingComboboxPicker";
import { SlotLabelsPicker } from "./SlotLabelsPicker";

function emptyRule(): VariantTransformRule {
  return { slots: [], fromNominal: "", toNominal: "" };
}

function materialIdForNominal(
  materials: readonly MfgMaterial[],
  nominal: string,
): string {
  const needle = nominal.trim().toLowerCase();
  if (!needle) return "";
  const byCode = materials.find((m) => m.code.trim().toLowerCase() === needle);
  if (byCode) return byCode.id;
  const byName = materials.find((m) => m.name.trim().toLowerCase() === needle);
  return byName?.id ?? "";
}

function NominalMaterialField({
  label,
  value,
  materials,
  options,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  materials: readonly MfgMaterial[];
  options: ComboboxOption<MfgMaterial>[];
  placeholder: string;
  onChange: (next: string) => void;
}) {
  const selectedId = materialIdForNominal(materials, value);
  return (
    <label className="block min-w-0 space-y-1">
      <span className="text-[11px] font-medium text-gray-600 dark:text-zinc-400">{label}</span>
      <input
        className={mfgInlineInputClass}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <ManufacturingComboboxPicker<MfgMaterial>
        value={selectedId}
        options={options}
        placeholder="Search catalog…"
        onPick={(opt) => onChange(opt.data.code)}
      />
    </label>
  );
}

/** Transform rules for the named-variant default materials effect. */
export function ManufacturingNamedVariantDefaultTransformRules({
  rules,
  materials,
  orgSlotLabels,
  prioritySlotLabels = [],
  onChange,
}: {
  rules: readonly VariantTransformRule[];
  materials: readonly MfgMaterial[];
  orgSlotLabels: readonly string[];
  prioritySlotLabels?: readonly string[];
  onChange: (next: VariantTransformRule[]) => void;
}) {
  const rows = rules.length > 0 ? [...rules] : [emptyRule()];
  const materialOptions = useMemo<ComboboxOption<MfgMaterial>[]>(
    () =>
      materials
        .filter((m) => m.is_active)
        .map((m) => ({
          key: m.id,
          label: m.code,
          sublabel: m.name,
          data: m,
        })),
    [materials],
  );

  function patch(index: number, patch: Partial<VariantTransformRule>) {
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  return (
    <div className="space-y-3">
      <div className="text-[11px] font-semibold text-gray-600 dark:text-zinc-400">
        Transform rules
      </div>
      {rows.map((rule, index) => (
        <div
          key={index}
          className="space-y-2 rounded-md border border-gray-100 p-2 dark:border-zinc-700"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold text-gray-600 dark:text-zinc-400">
              Transform rule {index + 1}
            </span>
            <button
              type="button"
              className={ghostBtnClass}
              disabled={rows.length <= 1}
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-zinc-400">
            Slots this rule targets — example-recipe slots first; expand for all org labels
          </div>
          <SlotLabelsPicker
            value={rule.slots.join(", ")}
            suggestions={orgSlotLabels}
            prioritySuggestions={prioritySlotLabels}
            placeholder="Slot labels"
            onChange={(csv) =>
              patch(index, {
                slots: csv
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
          />
          <div className="grid gap-2 sm:grid-cols-2">
            <NominalMaterialField
              label="From"
              value={rule.fromNominal}
              materials={materials}
              options={materialOptions}
              placeholder="size, code, or name"
              onChange={(fromNominal) => patch(index, { fromNominal })}
            />
            <NominalMaterialField
              label="To"
              value={rule.toNominal}
              materials={materials}
              options={materialOptions}
              placeholder="size, code, or name"
              onChange={(toNominal) => patch(index, { toNominal })}
            />
          </div>
        </div>
      ))}
      <button
        type="button"
        className={ghostBtnClass}
        onClick={() => onChange([...rows, emptyRule()])}
      >
        + Add transform rule
      </button>
    </div>
  );
}
